import { Portal, PortalOptions } from '@ariakit/react';
import {
  CSSProperties,
  RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import type { ComponentType } from 'react';

type UseDropdownPortal = {
  rootRef: RefObject<HTMLDivElement>;
  dropdownRef: RefObject<HTMLUListElement>;
  isOpen: boolean;
  isEnabled: boolean;
};
type PortalDimensions = Pick<CSSProperties, 'top' | 'left' | 'height' | 'width'>;

const MARGIN = 4;
const OFFSET_Y = 4;

const hiddenPosition: PortalDimensions = {
  top: 'auto',
  left: 'auto',
  height: 'auto', // Must be auto to ensure dropdown takes up full height when constructed
  width: 'auto',
};

const useDropdownPortal = ({
  rootRef,
  dropdownRef,
  isOpen,
  isEnabled,
}: UseDropdownPortal): {
    updatePortalDimensions: () => void;
    portalDimensions: PortalDimensions;
    portalWrapper: ComponentType<PortalOptions<'div'>> | null;
  } => {
  const [portalDimensions, setPortalDimensions] = useState(hiddenPosition);
  const parentRef = useRef<HTMLElement>();

  const updatePortalDimensions = useCallback(() => {
    if (!isOpen || !dropdownRef?.current || !rootRef?.current) {
      setPortalDimensions(hiddenPosition);
      return;
    }

    const el = dropdownRef.current;
    const rootEl = rootRef.current;
    const rootRect = rootEl.getBoundingClientRect();
    const width = rootEl.offsetWidth;
    const height = Math.ceil(Math.min(el.getBoundingClientRect().height, window.innerHeight / 2));
    const areaTop = MARGIN;
    const areaBottom = window.innerHeight - MARGIN;

    const { top, bottom, left } = rootRect;
    let y = bottom + OFFSET_Y;
    let yBottom = y + height;

    // Move up by diff
    if (yBottom > areaBottom) {
      // Enough space to place above
      if (height - OFFSET_Y < top) {
        y = areaTop - OFFSET_Y;
        // place below, overlap as needed
      } else {
        y += (areaBottom - yBottom);
      }
    }

    // Snap to top of dropdown button so it doesn't partially overflow.
    // It must be either fully below or fully over button. Removes any overlap from above calc
    if (y < bottom && y > top) {
      y = top;
    }

    // Lock to button position when scrolling down, but avoid going off-screen
    const minY = Math.max(areaTop, top - height - OFFSET_Y);
    if (y < minY) {
      y = minY;
    }

    // Update might be required due to above y changes
    yBottom = y + height;

    // Lock to button position when scrolling up, but avoid going off-screen
    if (yBottom > areaBottom) {
      y -= (yBottom - areaBottom);
    }

    setPortalDimensions({
      top: `${y}px`,
      left: `${left}px`,
      height: `${height}px`, // limited to max-content
      width: `${width}px`,
    });
  }, [dropdownRef, isOpen, rootRef]);

  // Check if this is within an overflow container, then that will be used as a scroll target
  const detectContainer = useCallback((): HTMLElement | null => {
    if (!rootRef.current) {
      return null;
    }

    let parent = rootRef.current.parentElement;
    while (parent) {
      if (parent.parentElement) {
        parent = parent.parentElement;
        const overflow = window.getComputedStyle(parent).overflowY;
        if (overflow === 'auto' || overflow === 'scroll') {
          break;
        }
      } else {
        parent = null;
        break;
      }
    }
    return parent;
  }, [rootRef]);

  useEffect(() => {
    if (!isEnabled) {
      window.removeEventListener('resize', updatePortalDimensions);
      document.removeEventListener('scroll', updatePortalDimensions);
      parentRef.current?.removeEventListener('scroll', updatePortalDimensions);
      return () => { };
    }
    parentRef.current = detectContainer() ?? undefined;

    window.addEventListener('resize', updatePortalDimensions);
    document.addEventListener('scroll', updatePortalDimensions);
    parentRef.current?.addEventListener('scroll', updatePortalDimensions);

    if (!isOpen && portalDimensions.height !== 'auto') {
      setPortalDimensions({
        ...portalDimensions,
        height: 'auto',
      });
    }

    return () => {
      window.removeEventListener('resize', updatePortalDimensions);
      document.removeEventListener('scroll', updatePortalDimensions);
      parentRef.current?.removeEventListener('scroll', updatePortalDimensions);
    };
  }, [isOpen, updatePortalDimensions, isEnabled, detectContainer, portalDimensions]);

  const portalWrapper = isEnabled ? Portal : null;
  return { updatePortalDimensions, portalDimensions, portalWrapper };
};

export default useDropdownPortal;
